import React, { useEffect, useState } from "react";
import "../styles/CustomerDisplayPage.css";
import "../styles/CustomerDisplayCashlessPopup.css";
import "../styles/TaxFreeMinimumNotice.css";

const DEFAULT_DISPLAY = {
  status: "standby",
  saleType: "normal",
  isTaxFree: false,
  taxFreeMode: "legacy",
  items: [],
  subtotal: 0,
  discountTotal: 0,
  tax: 0,
  taxBeforeExemption: 0,
  taxIncludedSubtotal: 0,
  taxExemptionAmount: 0,
  total: 0,
  paidCash: 0,
  paidCashless: 0,
  change: 0,
  taxFreeMinimumWarning: null,
};

const standbySlides = [
  { type: "payment" },
  {
    type: "image",
    src: "/media/customer_display/notorganize.png",
    alt: "荷物整理禁止",
  },
  {
    type: "image",
    src: "/media/customer_display/nocamera.png",
    alt: "撮影禁止",
  },
  {
    type: "image",
    src: "/media/customer_display/noeating.png",
    alt: "飲食禁止",
  },
  {
    type: "image",
    src: "/media/customer_display/mochikomikinshi.png",
    alt: "持ち込み禁止",
  },
  {
    type: "image",
    src: "/media/customer_display/mochikomika.png",
    alt: "ペットボトル可",
  },
  // パスポートと支払いnameの一致
  {
    type: "image",
    src: "/media/customer_display/payment_name.png",
    alt: "名前一致必項",
  },
  // リファンド方式の案内：4枚を順番に表示（各15秒）
  {
    type: "image",
    src: "/media/customer_display/refund_01.jpg",
    alt: "免税制度リファンド方式のご案内",
    duration: 15000,
    isRefundPoster: true,
  },
  {
    type: "image",
    src: "/media/customer_display/refund_02.jpg",
    alt: "購入時の手続き",
    duration: 15000,
    isRefundPoster: true,
  },
  {
    type: "image",
    src: "/media/customer_display/refund_03.jpg",
    alt: "出国時の税関検査",
    duration: 15000,
    isRefundPoster: true,
  },
  {
    type: "image",
    src: "/media/customer_display/refund_04.jpg",
    alt: "還付金の受け取り",
    duration: 15000,
    isRefundPoster: true,
  },
];

// リファンド方式の画像は2026年11月1日 0:00（日本時間）以降だけ表示する。
// 待機画面を開いたまま日付が変わっても、次回のスライド切替時に自動適用される。
const REFUND_POSTER_START_AT = new Date("2026-11-01T00:00:00+09:00").getTime();

const getActiveStandbySlides = () =>
  Date.now() >= REFUND_POSTER_START_AT
    ? standbySlides
    : standbySlides.filter((slide) => !slide.isRefundPoster);

const formatYen = (value) => {
  const num = Number(value || 0);
  return `¥${num.toLocaleString()}`;
};

const PaymentLogo = ({ file, alt }) => (
  <div className="CustomerDisplayPage-logo-card">
    <img
      src={`/media/payment_logos/${file}`}
      alt={alt}
      className="CustomerDisplayPage-logo-image"
    />
  </div>
);

const cardLogos = [
  ["visa.png", "VISA"],
  ["mastercard.png", "Mastercard"],
  ["jcb.png", "JCB"],
  ["amex.png", "AMEX"],
  ["diners.png", "Diners"],
  ["discover.png", "Discover"],
];

const cardLogosTaxFree = [
  ["visa.png", "VISA"],
  ["mastercard.png", "Mastercard"],
  ["jcb.png", "JCB"],
  ["amex.png", "AMEX"],
  ["diners.png", "Diners"],
  ["discover.png", "Discover"],
  ["unionpay.png", "UnionPay"],
];

const emoneyLogos = [
  ["applepay.png", "Apple Pay"],
  ["ic.png", "交通系IC"],
  ["id.png", "iD"],
  ["quicpay.png", "QUICPay"],
];

const qrLogosNormal = [
  ["paypay.png", "PayPay"],
  ["rakutenpay.png", "楽天Pay"],
  ["dpay.png", "d払い"],
  ["aupay.png", "au PAY"],
  ["smartcode.png", "Smart Code"],
  ["unionpayqr.png", "UnionPay QR"],
  ["alipay.png", "Alipay+"],
  ["wechatpay.png", "WeChat Pay"],
];

const qrLogosTaxFree = [
  ["paypay.png", "PayPay"],
  ["rakutenpay.png", "Rakuten Pay"],
  ["dpay.png", "d払い"],
  ["aupay.png", "au PAY"],
  ["smartcode.png", "Smart Code"],
  ["alipay.png", "Alipay+"],
  ["wechatpay.png", "WeChat Pay"],
  ["unionpayqr.png", "UnionPay QR"],
];

const LogoGroup = ({ title, logos }) => (
  <div className="CustomerDisplayPage-logo-group">
    <div className="CustomerDisplayPage-logo-group-title">{title}</div>
    <div className="CustomerDisplayPage-brand-grid">
      {logos.map(([file, alt]) => (
        <PaymentLogo key={file} file={file} alt={alt} />
      ))}
    </div>
  </div>
);

const CustomerDisplayPage = () => {
  const [displayData, setDisplayData] = useState(DEFAULT_DISPLAY);
  const [standbySlideIndex, setStandbySlideIndex] = useState(0);

  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem("customerDisplayData");
        if (!raw) {
          setDisplayData(DEFAULT_DISPLAY);
          return;
        }

        const parsed = JSON.parse(raw);
        setDisplayData({
          ...DEFAULT_DISPLAY,
          ...parsed,
          items: Array.isArray(parsed.items) ? parsed.items : [],
        });
      } catch (e) {
        console.error("客画面データ読み込み失敗:", e);
        setDisplayData(DEFAULT_DISPLAY);
      }
    };

    load();
    const timer = setInterval(load, 300);

    const handleStorage = (e) => {
      if (e.key === "customerDisplayData") load();
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      clearInterval(timer);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const legacyTaxFreeNotices = [
    "Tax-free purchase requires the customer and their passport, or a Visit Japan Web tax-free QR code.",
    "For card payments, the cardholder name must match the passport name.",
    "Due to the nature of the products, returns or exchanges for customer convenience are not accepted.",

    "면세 구매에는 본인과 본인의 여권 또는 Visit Japan Web 면세 QR 코드가 필요합니다.",
    "카드 결제 시 카드 명의와 여권 이름이 일치해야 합니다.",
    "고객님의 사정에 의한 반품 및 교환은 불가능합니다.",

    "免税购买需要本人及本人护照，或 Visit Japan Web 免税专用二维码。",
    "刷卡付款时，银行卡持卡人姓名必须与护照姓名一致。",
    "因商品性质特殊，恕不接受因顾客个人原因的退货或换货。",
  ];

  const refundTaxFreeNotices = [
    "This purchase is subject to the tax-free refund method.",
    "Consumption tax is collected at the time of purchase.",
    "The tax amount will be refunded after the required departure procedure.",

    "본 거래는 면세 환급 방식이 적용됩니다.",
    "구매 시 소비세를 포함한 금액을 결제합니다.",
    "출국 확인 절차 후 세금 상당액이 환급됩니다.",

    "本次购买适用免税退款方式。",
    "购买时需先支付包含消费税的金额。",
    "完成离境确认手续后，将退还相应税额。",
  ];

  const normalNotices = [
    "初期不良を除き、お客様都合による返品・交換はお受けできません。",
    "商品をバック等に入れられる場合は、防犯上の理由からレジカウンターでお願いしております。",
    "paypay決済は支払い完了画面の提示をお願い致します。",
  ];

  const standbyTaxFreeNotices =
    displayData.taxFreeMode === "refund"
      ? refundTaxFreeNotices
      : legacyTaxFreeNotices;

  const standbyNotices = [...normalNotices, ...standbyTaxFreeNotices];

  const isStandby = !displayData.items || displayData.items.length === 0;

  // レジで「免税小計」を押して税抜5,000円未満の場合のみ表示。
  // 同日追加購入の確認ボタンを押すとnullになり、300msの同期後に消える。
  const taxFreeMinimumWarning = displayData.taxFreeMinimumWarning;
  const showTaxFreeMinimumPopup =
    !isStandby &&
    displayData.isTaxFree &&
    displayData.status !== "complete" &&
    Number(taxFreeMinimumWarning?.remaining || 0) > 0;

  // レジ側の支払い方法は「現金」か「キャッシュレス」のみ。
  // paidCashless > 0 のときカードとQR（PayPayを含む）の案内を同時表示する。
  // 店舗QR画像やブランド別判定は不要。

  const showCashlessPopup =
    !showTaxFreeMinimumPopup &&
    !isStandby &&
    displayData.status !== "complete" &&
    Number(displayData.paidCashless || 0) > 0;

  // 11/1以降に増えるリファンド案内も含め、日付に応じた待機スライドを使う。
  const activeStandbySlides = getActiveStandbySlides();
  const currentStandbySlide =
    activeStandbySlides[standbySlideIndex] ?? activeStandbySlides[0];

  const isTaxFree = displayData.isTaxFree;

  const isLegacyTaxFree = isTaxFree && displayData.taxFreeMode === "legacy";

  const isRefundTaxFree = isTaxFree && displayData.taxFreeMode === "refund";

  const activeTaxFreeNotices = isRefundTaxFree
    ? refundTaxFreeNotices
    : legacyTaxFreeNotices;

  // 待機中のみスライドを切り替える。
  // 既存の案内は8秒、リファンド方式の4枚は文字が読めるよう各15秒。
  useEffect(() => {
    if (!isStandby) return;

    const slides = getActiveStandbySlides();
    const currentSlide = slides[standbySlideIndex] ?? slides[0];
    const timer = setTimeout(() => {
      // 日付が切り替わった場合も反映する（11月1日から4枚追加）。
      setStandbySlideIndex(
        (prev) => (prev + 1) % getActiveStandbySlides().length,
      );
    }, currentSlide.duration ?? 8000);

    return () => clearTimeout(timer);
  }, [isStandby, standbySlideIndex]);

  return (
    <div className="CustomerDisplayPage">
      <div className="CustomerDisplayPage-header">
        {isTaxFree ? (
          <div className="CustomerDisplayPage-notice-marquee">
            <div className="CustomerDisplayPage-notice-track taxfree">
              {[...activeTaxFreeNotices, ...activeTaxFreeNotices].map(
                (text, index) => (
                  <span key={index} className="CustomerDisplayPage-notice-item">
                    {text}
                  </span>
                ),
              )}
            </div>
          </div>
        ) : isStandby ? (
          <div className="CustomerDisplayPage-notice-marquee">
            <div className="CustomerDisplayPage-notice-track standby">
              {[...standbyNotices, ...standbyNotices].map((text, index) => (
                <span key={index} className="CustomerDisplayPage-notice-item">
                  {text}
                </span>
              ))}
            </div>
          </div>
        ) : displayData.status === "complete" ? (
          <>
            <div className="CustomerDisplayPage-logo">PASOMARU</div>
            <div className="CustomerDisplayPage-message">
              ありがとうございました
            </div>
          </>
        ) : (
          <div className="CustomerDisplayPage-notice-marquee">
            <div className="CustomerDisplayPage-notice-track normal">
              {[...normalNotices, ...normalNotices].map((text, index) => (
                <span key={index} className="CustomerDisplayPage-notice-item">
                  {text}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      {isStandby ? (
        <div className="CustomerDisplayPage-standby">
          {currentStandbySlide.type === "payment" ? (
            <>
              <div className="CustomerDisplayPage-standby-main">
                <img
                  src={`/media/system_images/pasoko.png`}
                  alt="pasoko"
                  className="CustomerDisplayPage-mascot"
                />
                いらっしゃいませ
                <img
                  src={`/media/system_images/maruo.png`}
                  alt="maruo"
                  className="CustomerDisplayPage-mascot"
                />
              </div>
              <div className="CustomerDisplayPage-standby-sub">
                ご利用可能なお支払い方法
              </div>

              <div className="CustomerDisplayPage-payment-guide">
                <div className="CustomerDisplayPage-payment-section">
                  <div className="CustomerDisplayPage-payment-section-title">
                    通常販売
                  </div>

                  <LogoGroup title="クレジットカード" logos={cardLogos} />
                  <LogoGroup title="電子マネー" logos={emoneyLogos} />
                  <LogoGroup title="QR決済" logos={qrLogosNormal} />
                </div>

                <div className="CustomerDisplayPage-payment-section taxfree">
                  <div className="CustomerDisplayPage-payment-section-title">
                    TAX FREE
                  </div>
                  <div className="CustomerDisplayPage-payment-note">
                    UnionPay Card and QR payments are available for tax-free
                    purchases.
                  </div>

                  <LogoGroup title="Credit Card" logos={cardLogosTaxFree} />
                  <LogoGroup title="E-Money" logos={emoneyLogos} />
                  <LogoGroup title="QR Payment" logos={qrLogosTaxFree} />
                </div>
              </div>
            </>
          ) : (
            <div className="CustomerDisplayPage-standby-poster">
              <img
                src={currentStandbySlide.src}
                alt={currentStandbySlide.alt}
                className="CustomerDisplayPage-standby-poster-image"
                style={
                  currentStandbySlide.isRefundPoster
                    ? {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain", // 縦長の画像を切らずに表示
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      ) : (
        <div className="CustomerDisplayPage-body">
          <div className="CustomerDisplayPage-items">
            <div className="CustomerDisplayPage-items-title">
              {isTaxFree ? "Items" : "商品一覧"}
            </div>

            <div className="CustomerDisplayPage-item-list">
              {displayData.items.slice(-8).map((item, index) => (
                <div
                  className="CustomerDisplayPage-item"
                  key={`${item.name || item.product_name}-${index}`}
                >
                  <div className="CustomerDisplayPage-item-name">
                    {item.name || item.product_name || "商品名未設定"}
                  </div>
                  <div className="CustomerDisplayPage-item-sub">
                    {item.quantity || 1}点 ×{" "}
                    {formatYen(item.price || item.unit_price)}
                  </div>
                  <div className="CustomerDisplayPage-item-price">
                    {formatYen(
                      item.subtotal ??
                        Number(item.price || item.unit_price || 0) *
                          Number(item.quantity || 1),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="CustomerDisplayPage-summary">
            <div className="CustomerDisplayPage-detail-box">
              <div className="CustomerDisplayPage-detail-title">
                {isTaxFree ? "Amount Details" : "金額詳細"}
              </div>

              <div className="CustomerDisplayPage-detail-row">
                <span>{isTaxFree ? "Subtotal" : "小計"}</span>
                <strong>{formatYen(displayData.subtotal)}</strong>
              </div>

              {displayData.discountTotal > 0 && (
                <div className="CustomerDisplayPage-detail-row discount">
                  <span>{isTaxFree ? "Discount" : "値引き合計"}</span>
                  <strong>-{formatYen(displayData.discountTotal)}</strong>
                </div>
              )}

              {/* 通常販売 */}
              {!isTaxFree && (
                <div className="CustomerDisplayPage-detail-row">
                  <span>消費税</span>
                  <strong>{formatYen(displayData.tax)}</strong>
                </div>
              )}

              {/* 従来免税 */}
              {isLegacyTaxFree && (
                <>
                  <div className="CustomerDisplayPage-detail-row">
                    <span>Consumption Tax</span>
                    <strong>{formatYen(displayData.taxBeforeExemption)}</strong>
                  </div>

                  <div className="CustomerDisplayPage-detail-row">
                    <span>Tax-included subtotal</span>
                    <strong>
                      {formatYen(displayData.taxIncludedSubtotal)}
                    </strong>
                  </div>

                  <div className="CustomerDisplayPage-detail-row exemption">
                    <span>Tax exemption</span>
                    <strong>
                      -{formatYen(displayData.taxExemptionAmount)}
                    </strong>
                  </div>
                </>
              )}

              {/* リファンド方式 */}
              {isRefundTaxFree && (
                <>
                  <div className="CustomerDisplayPage-detail-row">
                    <span>Consumption Tax</span>
                    <strong>{formatYen(displayData.tax)}</strong>
                  </div>

                  <div className="CustomerDisplayPage-detail-row">
                    <span>Tax-free Refund Method</span>
                    <strong>REFUND</strong>
                  </div>
                </>
              )}
            </div>

            <div className="CustomerDisplayPage-total-box">
              <div className="CustomerDisplayPage-total-label">
                {isLegacyTaxFree
                  ? "TAX FREE TOTAL"
                  : isRefundTaxFree
                    ? "TAX FREE / REFUND"
                    : "合計"}
              </div>
              <div className="CustomerDisplayPage-total">
                {formatYen(displayData.total)}
              </div>
            </div>

            {(displayData.paidCash > 0 || displayData.paidCashless > 0) && (
              <div className="CustomerDisplayPage-payment">
                {displayData.paidCash > 0 && (
                  <div>
                    <span>{isTaxFree ? "Cash" : "現金"}</span>
                    <strong>{formatYen(displayData.paidCash)}</strong>
                  </div>
                )}

                {displayData.paidCashless > 0 && (
                  <div>
                    <span>{isTaxFree ? "Cashless" : "キャッシュレス"}</span>
                    <strong>{formatYen(displayData.paidCashless)}</strong>
                  </div>
                )}

                <div className="CustomerDisplayPage-change">
                  <span>{isTaxFree ? "Change" : "おつり"}</span>
                  <strong>{formatYen(displayData.change)}</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 免税小計ボタン押下時の税抜5,000円未満案内：英語・韓国語・中国語で表示 */}
      {showTaxFreeMinimumPopup && (
        <div className="CustomerTaxFreeMinimum-overlay" role="alertdialog" aria-modal="true"
          aria-label="Tax-free minimum amount not reached">
          <div className="CustomerTaxFreeMinimum-card">
            <div className="CustomerTaxFreeMinimum-badge">TAX FREE INFORMATION</div>
            <div className="CustomerTaxFreeMinimum-title" lang="en">Minimum amount not reached</div>
            <div className="CustomerTaxFreeMinimum-info" lang="en">
              Tax-free shopping requires a minimum purchase of ¥5,000 before tax.
            </div>
            <div className="CustomerTaxFreeMinimum-remaining">
              <span lang="en">Additional purchase required (before tax)</span>
              <strong>¥{Math.ceil(Number(taxFreeMinimumWarning.remaining)).toLocaleString()}</strong>
            </div>
            <div className="CustomerTaxFreeMinimum-translations">
              <p lang="en">Please purchase an additional ¥{Math.ceil(Number(taxFreeMinimumWarning.remaining)).toLocaleString()} (before tax) to qualify for tax-free shopping.</p>
              <p lang="ko">면세 구매를 위한 최소 금액은 세전 5,000엔입니다. 면세를 받으시려면 세전 ¥{Math.ceil(Number(taxFreeMinimumWarning.remaining)).toLocaleString()}을(를) 더 구매해 주세요.</p>
              <p lang="zh-Hans">免税购物的最低消费金额为税前5,000日元。还需购买税前 ¥{Math.ceil(Number(taxFreeMinimumWarning.remaining)).toLocaleString()} 才能达到免税标准。</p>
            </div>
          </div>
        </div>
      )}

      {/* 現金/キャッシュレスの二択に対応。カード・QR・PayPayを同じ画面で案内する。 */}
      {showCashlessPopup && (
        <div
          className="CustomerDisplayCashlessPopup-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={isTaxFree ? "Tax-free cashless payment instructions" : "キャッシュレス決済のご案内"}
        >
          <div className={`CustomerDisplayCashlessPopup-card ${isTaxFree ? "taxfree" : "normal"}`}>
            <div className="CustomerDisplayCashlessPopup-caption">
              {isTaxFree ? "TAX FREE / CASHLESS PAYMENT" : "キャッシュレス決済 / CASHLESS PAYMENT"}
            </div>
            <div className="CustomerDisplayCashlessPopup-overview">
              {isTaxFree ? (
                <span lang="en">Please follow the instructions for your payment method.</span>
              ) : (
                <>
                  <span lang="ja">ご利用のお支払い方法に合わせてお手続きください。</span>
                  <small lang="en">Please follow the instructions for your payment method.</small>
                </>
              )}
            </div>

            <div className="CustomerDisplayCashlessPopup-methods">
              {/* カード払いは赤い端末(通常) / 黒い端末(免税) */}
              <section className="CustomerDisplayCashlessPopup-method CustomerDisplayCashlessPopup-card-method">
                <h2 className="CustomerDisplayCashlessPopup-method-title">
                  {isTaxFree ? "CARD PAYMENT" : "カード決済 / CARD"}
                </h2>
                <div className="CustomerDisplayCashlessPopup-method-content">
                  <div className="CustomerDisplayCashlessPopup-wait-text">
                    {isTaxFree ? (
                      <>
                        <p lang="en">Please wait while we prepare the payment terminal.</p>
                        <p lang="ko">결제 단말기를 준비 중입니다. 잠시만 기다려 주세요.</p>
                        <p lang="zh-Hans">正在准备支付终端，请稍候。</p>
                      </>
                    ) : (
                      <>
                        <p lang="ja">端末準備中です。少々お待ちください。</p>
                        <p lang="en">Please wait while we prepare the payment terminal.</p>
                      </>
                    )}
                  </div>
                  <div className="CustomerDisplayCashlessPopup-terminal-row">
                    <div
                      className={`CustomerDisplayCashlessPopup-terminal ${isTaxFree ? "black" : "red"}`}
                      aria-hidden="true"
                    >
                      <div className="CustomerDisplayCashlessPopup-terminal-screen" />
                      <div className="CustomerDisplayCashlessPopup-terminal-reader" />
                    </div>
                    <div className="CustomerDisplayCashlessPopup-terminal-label">
                      {isTaxFree ? "BLACK TERMINAL" : "赤い端末 / RED TERMINAL"}
                    </div>
                  </div>
                  <div className="CustomerDisplayCashlessPopup-instruction-text">
                    {isTaxFree ? (
                      <>
                        <p lang="en">When prompted, tap or insert your card into the <strong>BLACK terminal.</strong></p>
                        <p lang="ko">안내에 따라 <strong>검은색 단말기</strong>에 카드를 터치하거나 삽입해 주세요.</p>
                        <p lang="zh-Hans">根据提示，请在<strong>黑色支付终端</strong>上轻触或插入银行卡。</p>
                      </>
                    ) : (
                      <>
                        <p lang="ja">準備ができましたら、<strong>赤い端末</strong>にカードをタッチまたは差し込んでください。</p>
                        <p lang="en">When prompted, tap or insert your card into the <strong>RED terminal.</strong></p>
                      </>
                    )}
                  </div>
                </div>
              </section>

              {/* PayPay・その他QRは同じ案内。店舗のPayPay QR画像は表示しない。 */}
              <section className="CustomerDisplayCashlessPopup-method CustomerDisplayCashlessPopup-qr-method">
                <h2 className="CustomerDisplayCashlessPopup-method-title">
                  {isTaxFree ? "QR CODE PAYMENT" : "QR決済（PayPay・その他） / QR PAYMENT"}
                </h2>
                {isTaxFree ? (
                  <div className="CustomerDisplayCashlessPopup-qr-preparation taxfree-qr">
                    <div className="CustomerDisplayCashlessPopup-qr-mark" aria-hidden="true">▦</div>
                    <p lang="en">Please have your payment <strong>QR code</strong> ready.</p>
                    <p lang="ko">결제용 <strong>QR코드</strong>를 준비해 주세요.</p>
                    <p lang="zh-Hans">请准备好您的<strong>付款二维码</strong>。</p>
                  </div>
                ) : (
                  <div className="CustomerDisplayCashlessPopup-qr-preparation normal-qr">
                    <div className="CustomerDisplayCashlessPopup-qr-mark" aria-hidden="true">▦</div>
                    <p lang="ja">
                      PayPay・その他QR決済をご利用の場合は、
                      <strong>お支払い用のQRコードをご準備のうえ、お待ちください。</strong>
                    </p>
                    <p lang="en">
                      For PayPay and other QR payments, please <strong>have your payment QR code ready and wait.</strong>
                    </p>
                  </div>
                )}
              </section>
            </div>

            {/* 免税キャッシュレス決済のみ：名義不一致案内 */}
            {isTaxFree && (
              <div className="CustomerDisplayCashlessPopup-name-notice" role="note">
                <div className="CustomerDisplayCashlessPopup-name-notice-title">
                  IMPORTANT / 중요 안내 / 重要提示
                </div>
                <p lang="en">
                  <strong>For tax-free processing, please use a card or QR payment app
                  registered under the same name as your passport.</strong>
                </p>
                <p lang="ko">
                  <strong>면세 절차를 위해 여권 이름과 카드 명의 또는 QR 결제 앱에
                  등록된 이름이 일치하는지 확인해 주세요.</strong>
                </p>
                <p lang="zh-Hans">
                  <strong>办理免税手续时，请确认护照姓名与银行卡持卡人姓名或
                  二维码支付应用的注册姓名一致。</strong>
                </p>
              </div>
            )}

            <div className="CustomerDisplayCashlessPopup-amount">
              <span>{isTaxFree ? "Cashless Payment" : "キャッシュレス決済金額 / Cashless Payment"}</span>
              <strong>{formatYen(displayData.paidCashless)}</strong>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDisplayPage;
